import { Injectable, Inject } from '@angular/core';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { I18Model as I18ModelBuilder } from '@nx-ahs/script-shared/blocks/interfaces';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

export interface TranslatableField {
	fieldKey: string;
	fieldId: string;
	name?: string;
	label?: string;
	placeholder?: string;
	hint?: string;
	description?: string;
	options?: { index: number; label: string }[];
	[key: string]: any;
}

@Injectable({
	providedIn: 'root'
})
export class FormlyBuilderI18nService {
	constructor(
		private http: HttpClient,
		@Inject('environment') private environment: any
	) {}

	/**
	 * Extracts all translatable text from formly fields recursively
	 * @param fields The formly field configuration array
	 * @returns Array of translatable fields with their text content
	 */
	public extractTranslatableFields(fields: FormlyFieldConfig[]): TranslatableField[] {
		const translatableFields: TranslatableField[] = [];

		const processField = (field: FormlyFieldConfig) => {
			const props = field.props || field.templateOptions; // Support both new and legacy formly

			if (props) {
				const translatableField: TranslatableField = {
					fieldKey: (field.key as string) || '',
					fieldId: field.id || (field.key as string) || ''
				};

				// Extract translatable properties
				// Only extract name when it's actual display text, not just the field key echoed into props
				if (props.name && props.name !== (field.key as string)) translatableField.name = props.name;
				if (props.label) translatableField.label = props.label;
				if (props.placeholder) translatableField.placeholder = props.placeholder;
				if (props.hint) translatableField.hint = props.hint;
				if (props.description) translatableField.description = props.description;

				if (props.options && Array.isArray(props.options)) {
					const optionLabels = (props.options as any[])
						.map((opt, index) => ({ index, label: opt.label }))
						.filter((opt) => opt.label);
					if (optionLabels.length > 0) translatableField.options = optionLabels;
				}

				// Only add if there's something to translate
				if (Object.keys(translatableField).length > 2) {
					translatableFields.push(translatableField);
				}
			}

			// Recursively process fieldGroup
			if (field.fieldGroup && Array.isArray(field.fieldGroup)) {
				field.fieldGroup.forEach(processField);
			}

			// Recursively process fieldArray
			if (field.fieldArray && typeof field.fieldArray === 'object') {
				const fieldArray = field.fieldArray as any;
				if (fieldArray.fieldGroup && Array.isArray(fieldArray.fieldGroup)) {
					fieldArray.fieldGroup.forEach(processField);
				}
			}
		};

		fields.forEach(processField);
		return translatableFields;
	}

	/**
	 * Translates text using Google Translate API via backend
	 * @param text Text to translate
	 * @param targetLangCode Target language code (e.g., 'es', 'fr')
	 * @param sourceLangCode Source language code (default: 'en')
	 * @returns Translated text
	 */
	public async translateText(text: string, targetLangCode: string, sourceLangCode: string = 'en'): Promise<string> {
		if (!text || !text.trim()) return text;
		if (targetLangCode === sourceLangCode) return text;

		try {
			// Call the backend translation endpoint
			const result = await firstValueFrom(
				this.http.post<{ translatedText: string }>(`${this.environment.apiUrl}api/Contract/TranslateText`, {
					text: text,
					targetLangCode: targetLangCode,
					sourceLangCode: sourceLangCode
				})
			);

			return result?.translatedText || text;
		} catch (error) {
			console.error('Translation API error:', error);
			// Show more detailed error to user
			throw error; // Re-throw to let caller handle it
		}
	}

	/**
	 * Translates all extracted fields to target language
	 * @param fields Array of translatable fields
	 * @param targetLangCode Target language code
	 * @returns Map of field IDs to their translated content
	 */
	public async translateFields(
		fields: TranslatableField[],
		targetLangCode: string
	): Promise<Map<string, TranslatableField>> {
		const translations = new Map<string, TranslatableField>();

		for (const field of fields) {
			const translatedField: TranslatableField = {
				fieldKey: field.fieldKey,
				fieldId: field.fieldId
			};

			// Translate each property
			if (field.name) {
				translatedField.name = await this.translateText(field.name, targetLangCode);
			}
			if (field.label) {
				translatedField.label = await this.translateText(field.label, targetLangCode);
			}
			if (field.placeholder) {
				translatedField.placeholder = await this.translateText(field.placeholder, targetLangCode);
			}
			if (field.hint) {
				translatedField.hint = await this.translateText(field.hint, targetLangCode);
			}
			if (field.description) {
				translatedField.description = await this.translateText(field.description, targetLangCode);
			}

			if (field.options) {
				translatedField.options = [];
				for (const opt of field.options) {
					const translatedLabel = await this.translateText(opt.label, targetLangCode);
					translatedField.options.push({ index: opt.index, label: translatedLabel });
				}
			}

			translations.set(field.fieldId, translatedField);
		}

		return translations;
	}

	/**
	 * Applies translations to formly fields and stores them in i18Models
	 * @param fields The formly field configuration array
	 * @param translations Map of translations to apply
	 * @param langCode Language code being applied
	 */
	public applyTranslationsToFields(
		fields: FormlyFieldConfig[],
		translations: Map<string, TranslatableField>,
		langCode: string
	): void {
		const processField = (field: FormlyFieldConfig) => {
			const fieldId = field.id || (field.key as string) || '';
			const translation = translations.get(fieldId);

			if (translation) {
				const props = field.props || field.templateOptions;

				// Store translations in i18Models format for persistence in script JSON
				if (!props.i18Models) {
					props.i18Models = [];
				}

				// Store English as baseline if not already stored
				this.ensureEnglishInI18Models(props);

				// Update or add translations to i18Models (these will be saved in the script)
				this.updateOrAddI18Model(props.i18Models, 'name', langCode, translation.name);
				this.updateOrAddI18Model(props.i18Models, 'label', langCode, translation.label);
				this.updateOrAddI18Model(props.i18Models, 'placeholder', langCode, translation.placeholder);
				this.updateOrAddI18Model(props.i18Models, 'hint', langCode, translation.hint);
				this.updateOrAddI18Model(props.i18Models, 'description', langCode, translation.description);

				if (translation.options && props.options && Array.isArray(props.options)) {
					translation.options.forEach((opt) => {
						if (props.options[opt.index]) {
							this.updateOrAddI18Model(props.i18Models, `options[${opt.index}]`, langCode, opt.label);
							props.options[opt.index].label = opt.label;
						}
					});
				}

				// Apply translations to the field props for preview
				if (translation.name) props.name = translation.name;
				if (translation.label) props.label = translation.label;
				if (translation.placeholder) props.placeholder = translation.placeholder;
				if (translation.hint) props.hint = translation.hint;
				if (translation.description) props.description = translation.description;
			}

			// Recursively process fieldGroup
			if (field.fieldGroup && Array.isArray(field.fieldGroup)) {
				field.fieldGroup.forEach(processField);
			}

			// Recursively process fieldArray
			if (field.fieldArray && typeof field.fieldArray === 'object') {
				const fieldArray = field.fieldArray as any;
				if (fieldArray.fieldGroup && Array.isArray(fieldArray.fieldGroup)) {
					fieldArray.fieldGroup.forEach(processField);
				}
			}
		};

		fields.forEach(processField);
	}

	/**
	 * Ensures English text is stored in i18Models as the baseline
	 * @param props Field props containing current text and i18Models
	 */
	private ensureEnglishInI18Models(props: any): void {
		if (!props.i18Models) {
			props.i18Models = [];
		}

		// Store current English values if they don't exist in i18Models
		if (props.name) {
			const hasEnglishName = props.i18Models.some(
				(m: I18ModelBuilder) => m.i18key === 'name' && m.langCode === 'en'
			);
			if (!hasEnglishName) {
				props.i18Models.push({ i18key: 'name', langCode: 'en', text: props.name } as I18ModelBuilder);
			}
		}

		if (props.label) {
			const hasEnglishLabel = props.i18Models.some(
				(m: I18ModelBuilder) => m.i18key === 'label' && m.langCode === 'en'
			);
			if (!hasEnglishLabel) {
				props.i18Models.push({
					i18key: 'label',
					langCode: 'en',
					text: props.label
				} as I18ModelBuilder);
			}
		}

		if (props.placeholder) {
			const hasEnglishPlaceholder = props.i18Models.some(
				(m: I18ModelBuilder) => m.i18key === 'placeholder' && m.langCode === 'en'
			);
			if (!hasEnglishPlaceholder) {
				props.i18Models.push({
					i18key: 'placeholder',
					langCode: 'en',
					text: props.placeholder
				} as I18ModelBuilder);
			}
		}

		if (props.hint) {
			const hasEnglishHint = props.i18Models.some(
				(m: I18ModelBuilder) => m.i18key === 'hint' && m.langCode === 'en'
			);
			if (!hasEnglishHint) {
				props.i18Models.push({
					i18key: 'hint',
					langCode: 'en',
					text: props.hint
				} as I18ModelBuilder);
			}
		}

		if (props.description) {
			const hasEnglishDescription = props.i18Models.some(
				(m: I18ModelBuilder) => m.i18key === 'description' && m.langCode === 'en'
			);
			if (!hasEnglishDescription) {
				props.i18Models.push({
					i18key: 'description',
					langCode: 'en',
					text: props.description
				} as I18ModelBuilder);
			}
		}

		if (props.options && Array.isArray(props.options)) {
			(props.options as any[]).forEach((opt, index) => {
				if (opt.label) {
					const key = `options[${index}]`;
					const hasEnglish = props.i18Models.some(
						(m: I18ModelBuilder) => m.i18key === key && m.langCode === 'en'
					);
					if (!hasEnglish) {
						props.i18Models.push({ i18key: key, langCode: 'en', text: opt.label } as I18ModelBuilder);
					}
				}
			});
		}
	}

	/**
	 * Restores original English text from i18Models
	 * @param fields The formly field configuration array
	 */
	public restoreOriginalText(fields: FormlyFieldConfig[]): void {
		const processField = (field: FormlyFieldConfig) => {
			const props = field.props || field.templateOptions;

			if (props.i18Models && Array.isArray(props.i18Models)) {
				// Find English translations
				const enModels = props.i18Models.filter((m: I18ModelBuilder) => m.langCode === 'en');

				enModels.forEach((model: I18ModelBuilder) => {
					if (model.i18key === 'name' && model.text) props.name = model.text;
					if (model.i18key === 'label' && model.text) props.label = model.text;
					if (model.i18key === 'placeholder' && model.text) props.placeholder = model.text;
					if (model.i18key === 'hint' && model.text) props.hint = model.text;
					if (model.i18key === 'description' && model.text) props.description = model.text;
					if (model.i18key === 'autoFillAddressInputPlaceHolder' && model.text)
						props['autoFillAddressInputPlaceHolder'] = model.text;
					if (model.i18key.startsWith('options[') && model.text) {
						const match = model.i18key.match(/^options\[(\d+)\]$/);
						if (match && props.options?.[+match[1]]) props.options[+match[1]].label = model.text;
					}
				});
			}

			// Recursively process fieldGroup
			if (field.fieldGroup && Array.isArray(field.fieldGroup)) {
				field.fieldGroup.forEach(processField);
			}

			// Recursively process fieldArray
			if (field.fieldArray && typeof field.fieldArray === 'object') {
				const fieldArray = field.fieldArray as any;
				if (fieldArray.fieldGroup && Array.isArray(fieldArray.fieldGroup)) {
					fieldArray.fieldGroup.forEach(processField);
				}
			}
		};

		fields.forEach(processField);
	}

	/**
	 * Applies existing translations from i18Models
	 * @param fields The formly field configuration array
	 * @param langCode Language code to apply
	 */
	public applyExistingTranslations(fields: FormlyFieldConfig[], langCode: string): void {
		const processField = (field: FormlyFieldConfig) => {
			const props = field.props || field.templateOptions;

			if (props.i18Models && Array.isArray(props.i18Models)) {
				// Find translations for the target language
				const langModels = props.i18Models.filter((m: I18ModelBuilder) => m.langCode === langCode);

				langModels.forEach((model: I18ModelBuilder) => {
					if (model.i18key === 'name' && model.text) props.name = model.text;
					if (model.i18key === 'label' && model.text) props.label = model.text;
					if (model.i18key === 'placeholder' && model.text) props.placeholder = model.text;
					if (model.i18key === 'hint' && model.text) props.hint = model.text;
					if (model.i18key === 'description' && model.text) props.description = model.text;
					if (model.i18key === 'autoFillAddressInputPlaceHolder' && model.text)
						props['autoFillAddressInputPlaceHolder'] = model.text;
					if (model.i18key.startsWith('options[') && model.text) {
						const match = model.i18key.match(/^options\[(\d+)\]$/);
						if (match && props.options?.[+match[1]]) props.options[+match[1]].label = model.text;
					}
				});
			}

			// Recursively process fieldGroup
			if (field.fieldGroup && Array.isArray(field.fieldGroup)) {
				field.fieldGroup.forEach(processField);
			}

			// Recursively process fieldArray
			if (field.fieldArray && typeof field.fieldArray === 'object') {
				const fieldArray = field.fieldArray as any;
				if (fieldArray.fieldGroup && Array.isArray(fieldArray.fieldGroup)) {
					fieldArray.fieldGroup.forEach(processField);
				}
			}
		};

		fields.forEach(processField);
	}

	private updateOrAddI18Model(
		i18Models: I18ModelBuilder[],
		key: string,
		langCode: string,
		text: string | undefined
	): void {
		if (!text) return;

		const existingIndex = i18Models.findIndex((m: I18ModelBuilder) => m.i18key === key && m.langCode === langCode);

		if (existingIndex >= 0) {
			i18Models[existingIndex].text = text;
		} else {
			i18Models.push({
				i18key: key,
				langCode: langCode,
				text: text
			} as I18ModelBuilder);
		}
	}
}
